import {
  LOOP_SAMPLES,
  MUTE_RAMP_SECONDS,
  SAMPLE_RATE,
  TRACK_COUNT,
  VIDEO_SECONDS,
} from './constants'
import type { TempoPercent } from './constants'
import { stemUrls } from './media'

export type DecodedBank = AudioBuffer[]

export interface BankProgress {
  loaded: number
  total: number
}

/** Decoded audio is large. Evict least-recently-used banks past this budget. */
const BANK_CACHE_BUDGET_BYTES = 256 * 1024 * 1024

/**
 * Exact sample count of the musical loop when rendered at `percent` tempo.
 * Every preset divides 1,148,160 evenly, so this is always a whole number and
 * the loop stays sample-locked at every tempo.
 */
function loopSamplesAt(percent: TempoPercent): number {
  return Math.round((LOOP_SAMPLES * 100) / percent)
}

/** Full allocation, including the silent tail past the musical loop. */
function bufferSamplesAt(percent: TempoPercent): number {
  return Math.round((VIDEO_SECONDS * SAMPLE_RATE * 100) / percent)
}

export class AudioEngine {
  readonly ctx: AudioContext

  private readonly master: GainNode
  private readonly trackGains: GainNode[]
  private sources: AudioBufferSourceNode[] = []

  private readonly banks = new Map<TempoPercent, DecodedBank>()
  private readonly inflight = new Map<TempoPercent, Promise<DecodedBank>>()
  private readonly aborts = new Map<TempoPercent, AbortController>()
  private lru: TempoPercent[] = []

  /**
   * Peak of the summed eight-track mix, used to set master gain. The material
   * is identical across tempos, so this is measured once on the first bank and
   * reused — the original recomputed it per bank, an 8-array-lookup-per-sample
   * pass that stalled the main thread for ~100 ms at 25 % tempo.
   */
  private mixPeak: number | null = null

  private readonly muted: boolean[] = Array.from({ length: TRACK_COUNT }, () => false)

  private constructor(ctx: AudioContext) {
    this.ctx = ctx
    this.master = ctx.createGain()
    this.master.connect(ctx.destination)
    this.trackGains = Array.from({ length: TRACK_COUNT }, () => {
      const g = ctx.createGain()
      g.connect(this.master)
      return g
    })
  }

  static create(): AudioEngine {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) throw new Error('This browser does not support Web Audio.')
    // Pinning the rate to 48 kHz is what keeps the loop sample-exact; any
    // implicit resampling would smear the loop point.
    const ctx = new Ctor({ sampleRate: SAMPLE_RATE, latencyHint: 'interactive' })
    return new AudioEngine(ctx)
  }

  /**
   * iOS/WebKit will not start a context outside a user gesture, and resuming
   * alone is not always enough — a node has to actually run. This must be
   * called synchronously from within a gesture handler.
   */
  async unlock(): Promise<void> {
    if (this.ctx.state !== 'running') await this.ctx.resume()
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    gain.gain.value = 1e-6
    osc.connect(gain).connect(this.ctx.destination)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.025)
  }

  isBankReady(tempo: TempoPercent): boolean {
    return this.banks.has(tempo)
  }

  /**
   * Fetch and decode one tempo bank. Stems are fetched and decoded in
   * parallel — the original awaited each of the eight decodes in sequence,
   * which serialised work the browser is happy to do concurrently off-thread.
   */
  loadBank(tempo: TempoPercent, onProgress?: (p: BankProgress) => void): Promise<DecodedBank> {
    const cached = this.banks.get(tempo)
    if (cached) {
      this.touch(tempo)
      return Promise.resolve(cached)
    }
    const existing = this.inflight.get(tempo)
    if (existing) return existing

    const abort = new AbortController()
    this.aborts.set(tempo, abort)

    const task = (async (): Promise<DecodedBank> => {
      const urls = stemUrls(tempo)
      const total = urls.length
      let loaded = 0

      const contentSamples = loopSamplesAt(tempo)
      const allocSamples = bufferSamplesAt(tempo)

      const decoded = await Promise.all(
        urls.map(async (url) => {
          let res = await fetch(url, { signal: abort.signal, cache: 'force-cache' })
          if (!res.ok) {
            // A failure response served during deploy propagation can end up
            // in the HTTP cache carrying the year-long immutable header, and
            // force-cache would then replay it forever. One cache-punching
            // retry heals any client that cached such an error.
            res = await fetch(url, { signal: abort.signal, cache: 'reload' })
          }
          if (!res.ok) throw new Error(`Could not load ${url} (${res.status})`)
          const bytes = await res.arrayBuffer()
          const audio = await this.ctx.decodeAudioData(bytes)
          loaded += 1
          onProgress?.({ loaded, total })
          return audio
        }),
      )

      // Re-house each stem in an exactly-sized buffer. The allocation runs to
      // the full media length so that a loop region extending past the musical
      // loop reads silence rather than clamping — which lets loop points be set
      // by node properties alone, with no per-edit buffer copying.
      const bank = decoded.map((audio) => {
        const exact = this.ctx.createBuffer(1, allocSamples, SAMPLE_RATE)
        const dst = exact.getChannelData(0)
        const src = audio.getChannelData(0)
        dst.set(src.subarray(0, Math.min(src.length, contentSamples)))
        return exact
      })

      this.aborts.delete(tempo)
      this.inflight.delete(tempo)
      this.remember(tempo, bank)
      return bank
    })().catch((err: unknown) => {
      this.aborts.delete(tempo)
      this.inflight.delete(tempo)
      throw err
    })

    this.inflight.set(tempo, task)
    return task
  }

  /** Cancel an in-flight bank load that is no longer wanted. */
  cancelLoad(tempo: TempoPercent): void {
    this.aborts.get(tempo)?.abort()
    this.aborts.delete(tempo)
    this.inflight.delete(tempo)
  }

  /**
   * Schedule all eight stems to begin together.
   *
   * Looping is expressed purely through `loopStart`/`loopEnd` on the source
   * nodes. The previous build rebuilt eight trimmed `AudioBuffer`s on every
   * loop-point edit — roughly 37 MB of copying per drag of the slider — to
   * express something the Web Audio API already does natively and for free.
   *
   * Returns the context time the sources were scheduled for, which is exactly
   * what the transport clock needs to anchor itself.
   */
  start(
    bank: DecodedBank,
    musicalOffset: number,
    tempo: number,
    loop: { enabled: boolean; start: number; end: number },
    leadSeconds = 0.06,
  ): number {
    this.stopSources()
    const when = this.ctx.currentTime + leadSeconds

    this.sources = bank.map((buffer, i) => {
      const src = this.ctx.createBufferSource()
      src.buffer = buffer
      if (loop.enabled) {
        src.loop = true
        // Buffer time is musical time divided by tempo.
        src.loopStart = loop.start / tempo
        src.loopEnd = Math.min(loop.end / tempo, buffer.duration)
      }
      src.connect(this.trackGains[i]!)
      src.start(when, musicalOffset / tempo)
      return src
    })

    return when
  }

  /** Register a callback for the natural end of non-looping playback. */
  onEnded(fn: () => void): void {
    const first = this.sources[0]
    if (first) first.onended = fn
  }

  stopSources(): void {
    for (const src of this.sources) {
      try {
        src.onended = null
        src.stop()
        src.disconnect()
      } catch {
        /* already stopped */
      }
    }
    this.sources = []
  }

  setMute(index: number, muted: boolean): void {
    this.muted[index] = muted
    const gain = this.trackGains[index]
    if (!gain) return
    const now = this.ctx.currentTime
    const target = muted ? 0 : 1
    gain.gain.cancelScheduledValues(now)
    gain.gain.setValueAtTime(gain.gain.value, now)
    gain.gain.linearRampToValueAtTime(target, now + MUTE_RAMP_SECONDS)
  }

  isMuted(index: number): boolean {
    return this.muted[index] ?? false
  }

  /**
   * Set master gain so the summed mix peaks just below full scale. Measured
   * once, then reused for every bank.
   */
  applyNormalisation(bank: DecodedBank): void {
    if (this.mixPeak === null) this.mixPeak = measureMixPeak(bank)
    this.master.gain.value = Math.min(1, 0.92 / Math.max(1e-4, this.mixPeak))
  }

  async dispose(): Promise<void> {
    this.stopSources()
    for (const abort of this.aborts.values()) abort.abort()
    this.aborts.clear()
    this.inflight.clear()
    this.banks.clear()
    this.lru = []
    try {
      await this.ctx.close()
    } catch {
      /* already closed */
    }
  }

  private remember(tempo: TempoPercent, bank: DecodedBank): void {
    this.banks.set(tempo, bank)
    this.touch(tempo)
    this.evictIfNeeded()
  }

  private touch(tempo: TempoPercent): void {
    this.lru = this.lru.filter((t) => t !== tempo)
    this.lru.push(tempo)
  }

  private evictIfNeeded(): void {
    const bytesOf = (bank: DecodedBank) => bank.reduce((sum, b) => sum + b.length * 4, 0)
    let total = 0
    for (const bank of this.banks.values()) total += bytesOf(bank)

    while (total > BANK_CACHE_BUDGET_BYTES && this.lru.length > 1) {
      const victim = this.lru.shift()
      if (victim === undefined) break
      const bank = this.banks.get(victim)
      if (!bank) continue
      total -= bytesOf(bank)
      this.banks.delete(victim)
    }
  }
}

/**
 * Peak of the eight-track sum.
 *
 * The channel arrays are hoisted out of the sample loop. The original called
 * `getChannelData()` eight times *per sample* — about nine million accessor
 * calls over the 100 % bank, and four times that at 25 %.
 */
function measureMixPeak(bank: DecodedBank): number {
  const channels = bank.map((b) => b.getChannelData(0))
  const first = channels[0]
  if (!first) return 1
  const length = first.length
  let peak = 0
  for (let n = 0; n < length; n++) {
    let sum = 0
    for (let c = 0; c < channels.length; c++) sum += channels[c]![n]!
    const abs = sum < 0 ? -sum : sum
    if (abs > peak) peak = abs
  }
  return peak
}
